import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, Calculator, RefreshCw, Mail } from 'lucide-react';
import SEO from '@/components/seo/SEO';
import { Breadcrumbs } from '@/components/seo/Breadcrumbs';
import { EmailResultsDialog } from '@/components/tools/EmailResultsDialog';
import { trackSignupIntent } from '@/lib/tracking';

interface AreaInput {
  id: string;
  acres: string;
  type: string;
  cValue: number;
}

const SURFACE_TYPES = [
  { label: 'Concrete/Asphalt/Roofs', value: 0.95 },
  { label: 'Gravel (Packed)', value: 0.75 },
  { label: 'Bare Soil (Packed)', value: 0.60 },
  { label: 'Bare Soil (Loose)', value: 0.30 },
  { label: 'Grass/Turf (Steep Slope)', value: 0.35 },
  { label: 'Grass/Turf (Flat)', value: 0.15 },
  { label: 'Dense Forest/Natural Vegetation', value: 0.10 },
];

const RunoffCalculatorPage: React.FC = () => {
  const [areas, setAreas] = useState<AreaInput[]>([
    { id: '1', acres: '', type: '', cValue: 0 }
  ]);
  const [result, setResult] = useState<string | null>(null);
  const [isEmailOpen, setIsEmailOpen] = useState(false);

  const addRow = () => {
    setAreas([...areas, { id: crypto.randomUUID(), acres: '', type: '', cValue: 0 }]);
  };

  const removeRow = (id: string) => {
    if (areas.length > 1) {
      setAreas(areas.filter(a => a.id !== id));
    }
  };

  const updateRow = (id: string, field: keyof AreaInput, value: any) => {
    const newAreas = areas.map(a => {
      if (a.id === id) {
        const updated = { ...a, [field]: value };
        if (field === 'type') {
            const match = SURFACE_TYPES.find(t => t.label === value);
            updated.cValue = match ? match.value : 0;
        }
        return updated;
      }
      return a;
    });
    setAreas(newAreas);
  };

  const calculate = () => {
    let totalAcres = 0;
    let weightedSum = 0;

    areas.forEach(a => {
      const ac = parseFloat(a.acres);
      if (!isNaN(ac) && a.cValue > 0) {
        totalAcres += ac;
        weightedSum += (ac * a.cValue);
      }
    });

    if (totalAcres === 0) {
      setResult(null);
      return;
    }

    const weightedC = weightedSum / totalAcres;
    setResult(weightedC.toFixed(2));
  };

  const appSignUpUrl = import.meta.env.VITE_APP_URL 
    ? `${import.meta.env.VITE_APP_URL}/auth?mode=signup`
    : 'http://localhost:3000/auth?mode=signup';

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Weighted Runoff Coefficient Calculator",
    "description": "Calculate the weighted 'C' value for SWPPP hydrology analysis using the Rational Method.",
    "applicationCategory": "UtilitiesApplication",
    "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" }
  };

  return (
    <div className="min-h-screen bg-background font-sans selection:bg-blue-500/20">
      <SEO 
        title="Weighted Runoff Coefficient Calculator (EPA Appendix C)"
        description="Free tool to calculate the weighted 'C' value for your construction site SWPPP. Essential for sediment basin sizing."
        canonical="/tools/runoff-calculator"
        jsonLd={jsonLd}
      />

      <div className="bg-muted/30 pt-32 pb-20 border-b border-border">
        <div className="container px-4 text-center max-w-4xl mx-auto">
           <Breadcrumbs items={[
               { label: 'Resources', href: '/' },
               { label: 'Runoff Calculator', href: '/tools/runoff-calculator' }
           ]} />

           <div className="inline-flex items-center justify-center p-3 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full mb-6">
             <Calculator size={32} />
           </div>
           <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">Runoff Coefficient Calculator</h1>
           <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
             Calculate the "Weighted C" value for your SWPPP hydrology analysis. Based on <strong>EPA Appendix C</strong> methodology.
           </p>
        </div>
      </div>

      <div className="container px-4 py-16 max-w-4xl mx-auto">
        <div className="grid md:grid-cols-3 gap-8 items-start mb-24">
            
            {/* Calculator */}
            <Card className="md:col-span-2 border-border shadow-sm">
                <CardHeader>
                    <CardTitle>Site Surface Areas</CardTitle>
                    <CardDescription>Enter the acreage for each surface type on your site.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                    <div className="space-y-4">
                        {areas.map((area) => (
                            <div key={area.id} className="flex gap-3 items-end">
                                <div className="flex-1 space-y-1">
                                    <Label className="text-xs">Surface Type</Label>
                                    <Select value={area.type} onValueChange={(val) => updateRow(area.id, 'type', val)}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select type..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {SURFACE_TYPES.map(t => (
                                                <SelectItem key={t.label} value={t.label}>
                                                    {t.label} (C={t.value})
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="w-24 space-y-1">
                                    <Label className="text-xs">Acres</Label>
                                    <Input 
                                        type="number" 
                                        placeholder="0.0" 
                                        value={area.acres}
                                        onChange={(e) => updateRow(area.id, 'acres', e.target.value)}
                                        min="0"
                                    />
                                </div>
                                <Button 
                                    variant="ghost" 
                                    size="icon" 
                                    className="text-muted-foreground hover:text-destructive mb-0.5"
                                    onClick={() => removeRow(area.id)}
                                    disabled={areas.length === 1}
                                >
                                    <Trash2 size={16} />
                                </Button>
                            </div>
                        ))}
                    </div>

                    <div className="flex justify-between border-t pt-4">
                        <Button variant="outline" onClick={addRow} className="gap-2">
                            <Plus size={14} /> Add Surface Area
                        </Button>
                        <Button onClick={calculate} className="w-32 gap-2">
                             Calculate
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Results */}
            <div className="space-y-6">
               <Card className="bg-slate-900 text-white border-none shadow-xl">
                   <CardHeader>
                       <CardTitle className="text-slate-200 text-base">Weighted 'C' Value</CardTitle>
                   </CardHeader>
                   <CardContent>
                       <div className="text-5xl font-black tracking-tighter text-white mb-2">
                           {result || '---'}
                       </div>
                       <p className="text-xs text-slate-400 mb-6">
                           {result ? 'Use this value for your Rational Method (Q=CiA) calculations.' : 'Enter your site data to calculate.'}
                       </p>
                       {result && (
                           <Button variant="secondary" className="w-full text-xs" onClick={() => setIsEmailOpen(true)}>
                               <Mail className="mr-2 h-3 w-3" /> Email Me This
                           </Button>
                       )}
                   </CardContent>
               </Card>

               <div className="bg-muted/30 p-6 rounded-xl border border-border text-sm text-muted-foreground">
                   <h4 className="font-bold text-foreground mb-2 flex items-center gap-2"><RefreshCw size={14}/> Why this matters</h4>
                   <p className="mb-4">
                       The EPA requires estimating runoff before and after construction. A higher 'C' means more runoff volume, which requires larger sediment basins.
                   </p>
                   <Button variant="link" className="p-0 h-auto text-primary" asChild>
                       <a href={appSignUpUrl} onClick={() => trackSignupIntent('runoff_calculator_result')}>Automate your SWPPP &rarr;</a>
                   </Button>
               </div>
            </div>
        </div>

        {/* SEO CONTENT SECTION */}
        <div className="prose dark:prose-invert max-w-none pt-8 border-t border-border">
            <h2>Understanding the Runoff Coefficient</h2>
            <p>
                The <strong>Runoff Coefficient (C)</strong> is a dimensionless number between 0 and 1 that represents the percentage of rainfall that becomes runoff versus the amount that infiltrates the ground.
            </p>
            <ul>
                <li><strong>C = 0.95:</strong> Almost all rain runs off (e.g., Concrete, Asphalt).</li>
                <li><strong>C = 0.15:</strong> Most rain soaks in (e.g., Grass, Forest).</li>
            </ul>

            <h3>Why calculate a "Weighted" C?</h3>
            <p>
                Construction sites are rarely uniform. You might have 2 acres of paved parking, 1 acre of building roof, and 5 acres of bare soil. To properly size your <strong>Sediment Basins</strong> and drainage channels, you must calculate a single 'C' value that represents the average of the entire site area.
            </p>

            <h3>The Rational Method Formula</h3>
            <p>
                Engineers use the Rational Method to estimate peak discharge (Q):
            </p>
            <pre>Q = C Ã— i Ã— A</pre>
            <ul>
                <li><strong>Q:</strong> Peak discharge (cubic feet per second)</li>
                <li><strong>C:</strong> Weighted Runoff Coefficient (from this calculator)</li>
                <li><strong>i:</strong> Rainfall intensity (inches per hour)</li>
                <li><strong>A:</strong> Drainage area (acres)</li>
            </ul>
            <p>
                If your 'C' value is too low, you will undersize your basins, leading to overflows and EPA fines during storm events.
            </p>
        </div>
      </div>

      <EmailResultsDialog 
        isOpen={isEmailOpen}
        onOpenChange={setIsEmailOpen}
        toolName="Runoff Calculator"
        resultSummary={`Weighted C = ${result}`}
        resultDetails={{ areas }}
      />
    </div>
  );
};

export default RunoffCalculatorPage;
